import { toast } from "sonner";
import { Copy } from "lucide-react";
import { gbp, miles } from "@/lib/api";
import { useSite } from "@/lib/site";

const tag = (s) => `#${String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "")}`;

const hashtags = (v) => [...new Set([
  "#cirencester4x4", tag(v.make), tag(`${v.make}${v.model}`), tag(v.model),
  "#4x4", "#suv", "#cotswolds", "#gloucestershire", "#usedcars", "#carsforsale",
])].join(" ");

const name = (v) => `${v.year} ${v.make} ${v.model}${v.derivative ? ` ${v.derivative}` : ""}`;

const intro = (v) => (v.full_description || "").split("\n").map((p) => p.trim()).find(Boolean)?.replace(/\s*Now sold\.?$/i, "") || "";

const spec = (v) => [miles(v.mileage), v.transmission, v.fuel_type].filter(Boolean).join(" · ");

const trim = (v) => (v.colour && v.interior_colour ? `${v.colour} over ${v.interior_colour}` : v.colour);

const features = (v) => (v.key_features || [])
  .map((f) => f.replace(/\s*\([A-Z0-9-]{6,}\)/g, ""))
  .filter((f) => f !== v.colour && !(v.short_description || "").includes(f))
  .filter((f) => !(v.interior_colour && /interior/i.test(f) && f.toLowerCase().startsWith(v.interior_colour.toLowerCase())))
  .slice(0, 5)
  .map((f) => `✔️ ${f}`);

export function buildCaption(v, phone) {
  if (v.status === "sold") {
    return [
      "Sold 🌿",
      `Our ${name(v)}${v.colour && !(v.short_description || "").includes(v.colour) && !intro(v).includes(v.colour) ? ` in ${v.colour}` : ""} has found a new home.`,
      intro(v) || v.short_description,
      "Looking for something similar? We hand-pick every 4x4 and SUV we offer, and we'd be glad to hear what you're after.",
      `📩 Send us a DM, WhatsApp ${phone} or tap the link in bio to see current stock.\n📍 Cirencester, the Cotswolds`,
      hashtags(v),
    ].filter(Boolean).join("\n\n");
  }
  const opener = v.status === "reserved" ? "Now reserved ✨" : "Just arrived 🌿";
  const details = [`✔️ ${spec(v)}`, trim(v) && `✔️ ${trim(v)}`].filter(Boolean);
  return [
    opener,
    name(v),
    v.short_description,
    intro(v),
    [...features(v), ...details].join("\n"),
    v.price ? gbp(v.price) : null,
    "Carefully selected. Properly prepared.",
    v.status === "reserved"
      ? `📩 Want to be next in line? DM us or WhatsApp ${phone} to be the first to know if it becomes available.\n📍 Cirencester, the Cotswolds`
      : `📩 Interested? Send us a DM, WhatsApp ${phone} or tap the link in bio to arrange a viewing before it goes.\n📍 Cirencester, the Cotswolds`,
    hashtags(v),
  ].filter(Boolean).join("\n\n");
}

export function CopyCaptionButton({ vehicle }) {
  const { phone } = useSite();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(buildCaption(vehicle, phone));
      toast.success("Instagram caption copied");
    } catch {
      toast.error("Could not copy. Please allow clipboard access and try again.");
    }
  };
  return (
    <button onClick={copy} aria-label="Copy Instagram caption" title="Copy Instagram caption" data-testid={`copy-caption-${vehicle.slug}`}>
      <Copy className="h-4 w-4" />
    </button>
  );
}
